import moment from 'moment';
import React, { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import Form from 'react-bootstrap/Form';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import { FaRegSave } from 'react-icons/fa';
import { MdAdd, MdArrowBack, MdKeyboardReturn } from 'react-icons/md';
import { useLocation, useSearchParams } from 'react-router-dom';
import { toast } from '../../toasts';
import CustomTypeahead from '../../components/CustomTypeahead';
import { Button, Checkbox, FormField, Section, Select, controlClass } from '../../components/EntityList';
import FormatedNumberInput from '../../components/FormatedNumberInput';
import DetailPageLoader from '../../components/DetailPageLoader/DetailPageLoader';
import accountService from '../../services/account.service';
import assetSaleService from '../../services/assetSale.service';
import contractService from '../../services/contract.service';
import debtService from '../../services/debt.service';
import debtConceptService from '../../services/debtConcept.service';
import { currencies, dueDays, getError, monthOptions, periodicities } from '../../utils';
import { useLabels } from '../../hooks/useLabels';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import PropTypes from 'prop-types';
import {
	FRONTEND_LEGACY_DEBT_META,
	FALLBACK_DEBT_CONCEPT_NAMES,
	behaviorNeedsCapital,
	behaviorNeedsDues,
	behaviorNeedsInterest,
	buildConceptData,
	buildConceptMeta,
	depositNeedsDues,
	getMeta,
	normalizeConceptName,
	validateByMeta,
} from '../../utils/conceptMeta';

// Concepts a contract can carry, when no active concepts fetch succeeds.
const CONTRACT_FALLBACK_CONCEPTS = ['alquileres', 'garantia-de-alquiler', 'gasto-de-activo-fijo'];
// An asset-sale debt is always an installment sale — no fetch needed.
const ASSET_SALE_CONCEPTS = ['ventas-a-plazo'];

const REQUIRED_FIELD_MESSAGE = 'Este campo es obligatorio.';

// Maps the Spanish labels validateCommonFields()/validateByMeta() already
// produce onto the field-error keys used for inline messages below each
// input, so the missing-field logic stays in one place.
const FIELD_ERROR_KEYS = {
	'Fecha': 'date',
	'Concepto': 'concept',
	'Otro Concepto': 'otroConcepto',
	'Precio por usuario': 'pricePerUser',
	'Monto': 'amount',
	'Cuenta': 'owner',
	'Moneda': 'currency',
	'Datos generación cuota': 'firstDueExpiration',
	'Contrato': 'contract',
	'Cuotas': 'dues',
	'Capital': 'capital',
	'Interés': 'interest',
	'Periodicidad': 'periodicity',
	'Venta de activo fijo': 'assetSale',
};

const FieldError = ({ message }) =>
	message ? <p className='tw:mt-1 tw:mb-0 tw:text-[12px] tw:text-danger'>{message}</p> : null;

FieldError.propTypes = {
	message: PropTypes.string,
};

const reducer = (state, action) => {
	switch (action.type) {
	case 'FETCH_REQUEST':
		return { ...state, loading: true, error: '' };
	case 'FETCH_SUCCESS':
		return { ...state, loading: false, error: '' };
	case 'FETCH_FAIL':
		return { ...state, loading: false, error: action.payload };
	case 'FETCH_ACCOUNT_REQUEST':
		return { ...state, loadingFetch: true };
	case 'FETCH_ACCOUNT_SUCCESS':
		return { ...state, loadingFetch: false };
	case 'FETCH_ACCOUNTS_SUCCESS':
		return { ...state, accounts: action.payload };
	case 'FETCH_CONTRACT_REQUEST':
		return { ...state, loadingFetch: true };
	case 'FETCH_CONTRACTS_SUCCESS':
		return { ...state, contracts: action.payload };
	case 'FETCH_ASSETSALE_REQUEST':
		return { ...state, loadingFetch: true };
	case 'FETCH_ASSETSALES_SUCCESS':
		return { ...state, assetSales: action.payload };
	case 'SAVE_REQUEST':
		return { ...state, saving: true, error: '' };
	case 'SAVE_SUCCESS':
		return { ...state, saving: false, error: '' };
	case 'SAVE_FAIL':
		return { ...state, saving: false, error: action.payload };
	default:
		return state;
	}
};

/**
 * Full-page debt create/edit form (see docs/ui/entity-list-pattern.md and
 * screens/Accounts/AccountEditor.jsx for the pattern this mirrors). Renders
 * only the form body — the page shell (EntityList/PageHead) lives in the
 * page components that use this (NewDebt.jsx, EditDebt.jsx, and the
 * contextual create/edit routes reached from the account/contract/asset-sale
 * tabs).
 */
const DebtEditor = ({
	mode = 'create',
	debtId = null,
	initialData = null,
	onCancel,
	onSaved,
	cancelLabel = 'Cancelar',
	saveLabel,
	onReturn,
	returnLabel,
	preloadAccountId = null,
	preloadContractId = null,
	preloadAssetSaleId = null,
}) => {
	const { t } = useLabels();
	const isEditing = mode === 'edit';
	const currentDate = moment.utc();

	const location = useLocation();
	const [searchParams, setSearchParams] = useSearchParams();
	// Ids/concept injected via ?accountId=/?contractId=/?assetSaleId=/?concept=
	// after a "crear nueva cuenta/contrato/venta" round trip (see NewAccount.jsx
	// / NewContract.jsx / NewAssetSale.jsx). Kept in sync with the actual
	// selection (see syncInjectedParam below) rather than cleared outright —
	// linkToCreate carries location.search forward, so a second round trip
	// keeps every current selection instead of losing it.
	const injectedAccountId = searchParams.get('accountId');
	const injectedContractId = searchParams.get('contractId');
	const injectedAssetSaleId = searchParams.get('assetSaleId');
	const injectedConcept = searchParams.get('concept');
	const consumedAccountId = useRef(null);
	const consumedContractId = useRef(null);
	const consumedAssetSaleId = useRef(null);
	const consumedConcept = useRef(null);
	// Ids present at mount. syncInjectedParam rewrites the live params as the
	// user edits, so the edit-mode hydrate guards must key off this stable
	// snapshot instead of the (now-mutable) params above.
	const injectedAtMount = useRef({
		accountId: injectedAccountId,
		contractId: injectedContractId,
		assetSaleId: injectedAssetSaleId,
	});

	// Keep the URL mirroring the current selection for any field a "crear
	// nueva..." round trip can fill. The param is the only state that survives
	// the remount such a trip causes, so rewrite it on a manual pick (so that
	// choice survives the next trip) and drop it when the field is cleared.
	// Without this, a stale id from an earlier trip rides along in returnTo
	// and gets re-injected on top of the manual choice.
	const syncInjectedParam = (key, id, consumedRef) => {
		const nextValue = id || null;
		if (searchParams.get(key) === nextValue) return;
		// Pre-mark as consumed so the injection effect doesn't re-fetch a
		// record we already hold in state.
		if (nextValue) consumedRef.current = nextValue;
		setSearchParams((prev) => {
			const next = new URLSearchParams(prev);
			if (nextValue) next.set(key, nextValue);
			else next.delete(key);
			return next;
		}, { replace: true });
	};

	const [{ loading, saving, error, accounts, contracts, assetSales }, dispatch] = useReducer(reducer, {
		loading: true,
		saving: false,
		error: '',
		accounts: [],
		contracts: [],
		assetSales: [],
	});

	// Edit mode fetches the debt by id when the caller didn't already supply
	// initialData (mirrors AccountEditor.jsx's accountId-fetch effect).
	const [fetchedDebt, setFetchedDebt] = useState(null);
	const resolvedInitialData = initialData || fetchedDebt;

	const [capital, setCapital] = useState();
	const [amount, setAmount] = useState(0);
	const [interest, setInterest] = useState(0);
	const [selectedConcept, setSelectedConcept] = useState();
	const [concept, setConcept] = useState();
	const [dues, setDues] = useState();
	const [date, setDate] = useState();
	const [contract, setContract] = useState();
	const [periodicity, setPeriodicity] = useState('mensual');
	const [owner, setOwner] = useState();
	const [disabledInput, setDisabledInput] = useState(true);
	const [previousDebt, setPreviousDebt] = useState();
	const [previousDebtDate, setPreviousDebtDate] = useState();
	const [currency, setCurrency] = useState();
	const [endDate, setEndDate] = useState();
	const [disabledAmount, setDisabledAmount] = useState(false);
	const [disabledEndDate, setDisabledEndDate] = useState(false);
	const [filteredContracts, setFilteredContracts] = useState();
	const [isInvalid, setIsInvalid] = useState(false);
	const [hasPreviousDebts, setHasPreviousDebts] = useState(false);
	const [isPropertyInvalid, setIsPropertyInvalid] = useState(false);
	const [isAssetInvalid, setIsAssetInvalid] = useState(false);
	const [assetSale, setAssetSale] = useState();
	const [periodicityNumMonths, setPeriodicityNumMonths] = useState();
	const [createWithPreviousDues, setCreateWithPreviousDues] = useState(false);
	const [createWithPreviousPayments, setCreateWithPreviousPayments] = useState(false);
	const [dueDay, setDueDay] = useState('10');
	const [firstDueExpiration, setFirstDueExpiration] = useState();
	const [numMonths, setNumMonths] = useState(0);
	const [selectedMonthOption, setSelectedMonthOption] = useState('Mes siguiente');
	const [concepts, setConcepts] = useState([]);
	const [conceptMap, setConceptMap] = useState({});
	const [conceptMetaMap, setConceptMetaMap] = useState({});
	const [billingType, setBillingType] = useState('fixed');
	const [pricePerUser, setPricePerUser] = useState();
	const [initialUserCount, setInitialUserCount] = useState();
	const [hydrated, setHydrated] = useState(false);
	const [fieldErrors, setFieldErrors] = useState({});

	const clearFieldError = (field) => {
		setFieldErrors((prevErrors) => {
			if (!prevErrors[field]) return prevErrors;
			const nextErrors = { ...prevErrors };
			delete nextErrors[field];
			return nextErrors;
		});
	};

	// Metadata for the selected concept drives which blocks render and how the
	// form validates. Legacy slugs resolve through the frontend legacy map when
	// the concept doc carries no behavior.
	const meta = getMeta(selectedConcept, conceptMetaMap, FRONTEND_LEGACY_DEBT_META);
	const isLegacySpecial = (key) =>
		!!key && Object.prototype.hasOwnProperty.call(FRONTEND_LEGACY_DEBT_META, key);
	// Concepts with dedicated real-estate flows keep them; every other periodic
	// concept (and any concept flagged allowsPerUser) gets the generic billing
	// block (fixed amount or per-user licensing).
	const showBillingBlock =
		!!selectedConcept &&
		selectedConcept !== 'otro' &&
		(!!meta.allowsPerUser || (meta.behavior === 'periodic' && !isLegacySpecial(selectedConcept)));
	const isPerUser = showBillingBlock && billingType === 'perUser';

	// Keep a just-created record selectable even though the underlying list
	// fetch ran before it existed (mirrors ContractEditor.jsx's accountOptions
	// / assetOptions).
	const accountOptions = useMemo(
		() => (owner?._id && !accounts.some((a) => a._id === owner._id) ? [owner, ...accounts] : accounts),
		[accounts, owner],
	);
	const activeContracts = filteredContracts || contracts;
	const contractOptions = useMemo(() => {
		const selectedContract = contract?.[0];
		return selectedContract?._id && !activeContracts.some((c) => c._id === selectedContract._id)
			? [selectedContract, ...activeContracts]
			: activeContracts;
	}, [activeContracts, contract]);
	const assetSaleOptions = useMemo(() => {
		const selectedAssetSale = assetSale?.[0];
		return selectedAssetSale?._id && !assetSales.some((s) => s._id === selectedAssetSale._id)
			? [selectedAssetSale, ...assetSales]
			: assetSales;
	}, [assetSales, assetSale]);

	// Contract debts are restricted to the active concepts flagged
	// requiresContract; asset-sale debts are always ventas-a-plazo. Resolved
	// here since the tabs that used to own this (ContractDebts.jsx) no longer
	// pass allowedConcepts as a prop — DebtEditor derives it from context.
	const resolveAllowedConcepts = async () => {
		if (preloadContractId) {
			try {
				const result = await debtConceptService.list({ active: true, requiresContract: true });
				const keys = (result.data || [])
					.filter((c) => c && (c.key || c.name))
					.map((c) => c.key || normalizeConceptName(c.name));
				return keys.length > 0 ? keys : CONTRACT_FALLBACK_CONCEPTS;
			} catch (err) {
				console.error('Error fetching contract debt concepts:', getError(err));
				return CONTRACT_FALLBACK_CONCEPTS;
			}
		}
		if (preloadAssetSaleId) {
			return ASSET_SALE_CONCEPTS;
		}
		return null;
	};

	const fetchDebtConcepts = async () => {
		const allowedConcepts = await resolveAllowedConcepts();
		try {
			const result = await debtConceptService.list({ active: true });
			const activeConcepts = result.data
				.sort((a, b) => (a.order || 0) - (b.order || 0));
			setConceptMetaMap(buildConceptMeta(activeConcepts));

			const map = {};
			let conceptValues = activeConcepts
				.filter((c) => c.name)
				.map((c) => {
					const normalized = normalizeConceptName(c.name);
					map[normalized] = c.name;
					return normalized;
				});

			if (allowedConcepts) {
				// Always keep the existing concept in edit mode even if it falls
				// outside the allowed list (mirrors the original prop contract).
				const existingConcept =
					isEditing && resolvedInitialData?.concept ? normalizeConceptName(resolvedInitialData.concept) : null;
				const conceptsToInclude =
					existingConcept && !allowedConcepts.includes(existingConcept)
						? [...allowedConcepts, existingConcept]
						: allowedConcepts;

				conceptValues = conceptValues.filter((c) => conceptsToInclude.includes(c));
				const filteredMap = {};
				conceptsToInclude.forEach((c) => {
					if (map[c]) {
						filteredMap[c] = map[c];
					}
				});
				setConceptMap(filteredMap);
			} else {
				setConceptMap(map);
			}

			setConcepts(conceptValues);
		} catch (error) {
			console.error('Error fetching debt concepts:', error);
			toast.error(t('msg.conceptsLoadFailed'));
			setConceptMetaMap({});
			// Legacy slugs (only those with a display name; the unaccented
			// 'prestamos-interes' duplicate is skipped).
			let fallbackConcepts = Object.keys(FRONTEND_LEGACY_DEBT_META)
				.filter((key) => FALLBACK_DEBT_CONCEPT_NAMES[key]);
			const fallbackMap = { ...FALLBACK_DEBT_CONCEPT_NAMES };

			if (allowedConcepts) {
				fallbackConcepts = fallbackConcepts.filter((c) => allowedConcepts.includes(c));
			}

			setConceptMap(fallbackMap);
			setConcepts(fallbackConcepts);
		}
	};

	const fetchAccountData = async () => {
		try {
			dispatch({ type: preloadAccountId ? 'FETCH_ACCOUNT_REQUEST' : 'FETCH_REQUEST' });
			if (preloadAccountId) {
				const result = await accountService.getDetails(preloadAccountId);
				setOwner(result.data);
				setCurrency(result.data.currency);
				dispatch({ type: 'FETCH_ACCOUNT_SUCCESS', payload: result.data });
			} else {
				const result = await accountService.list();
				dispatch({ type: 'FETCH_ACCOUNTS_SUCCESS', payload: result.data });
			}
		} catch (error) {
			dispatch({ type: 'FETCH_FAIL', payload: error.message });
			console.error(error);
			toast.error(t('msg.accountLoadFailed'));
		}
	};

	const fetchContractsData = async () => {
		try {
			dispatch({ type: preloadContractId ? 'FETCH_CONTRACT_REQUEST' : 'FETCH_REQUEST' });
			if (preloadContractId) {
				setSelectedConcept('alquileres');
				setConcept('alquileres');
				const result = await contractService.getDetails(preloadContractId);
				setContract([result.data]);
				setOwner(result.data.owner);
				setAmount(result.data.amount);
				setCurrency(result.data.currency);
				setEndDate(result.data.periodEnd);
				setDisabledEndDate(true);
				dispatch({ type: 'FETCH_CONTRACT_SUCCESS', payload: result.data });
			} else if (preloadAccountId) {
				const result = await contractService.getContractsForAccount(preloadAccountId);
				setFilteredContracts(result.data);
			} else {
				const result = await contractService.list();
				dispatch({ type: 'FETCH_CONTRACTS_SUCCESS', payload: result.data });
			}
		} catch (error) {
			dispatch({ type: 'FETCH_FAIL', payload: error.message });
			console.error(error);
			toast.error(t('msg.contractLoadFailed'));
		}
	};

	const fetchAssetSalesData = async () => {
		try {
			dispatch({ type: preloadAssetSaleId ? 'FETCH_ASSETSALE_REQUEST' : 'FETCH_REQUEST' });
			if (preloadAssetSaleId) {
				const result = await assetSaleService.getDetails(preloadAssetSaleId);
				setOwner(result.data.purchaser);
				setCurrency(result.data.currency);
				setConcept('ventas-a-plazo');
				setSelectedConcept('ventas-a-plazo');
				setAssetSale([result.data]);
				setDisabledInput(false);
				dispatch({ type: 'FETCH_ASSETSALE_SUCCESS', payload: result.data });
			} else {
				const result = await assetSaleService.list();
				dispatch({ type: 'FETCH_ASSETSALES_SUCCESS', payload: result.data });
			}
		} catch (error) {
			dispatch({ type: 'FETCH_FAIL', payload: error.message });
			console.error(error);
			toast.error(t('msg.assetSaleLoadFailed'));
		}
	};

	const fetchDebtById = async () => {
		if (initialData || !debtId) {
			return;
		}
		try {
			const result = await debtService.getDetails(debtId);
			setFetchedDebt(result.data);
		} catch (error) {
			dispatch({ type: 'FETCH_FAIL', payload: getError(error) });
			console.error(error);
			toast.error(t('msg.debtLoadFailed') || 'No se pudo obtener la deuda');
		}
	};

	useEffect(() => {
		const fetchData = async () => {
			try {
				dispatch({ type: 'FETCH_REQUEST' });
				await Promise.all([
					fetchDebtById(),
					fetchAccountData(),
					fetchContractsData(),
					fetchAssetSalesData(),
					fetchDebtConcepts(),
				]);
				dispatch({ type: 'FETCH_SUCCESS' });
			} catch (ex) {
				console.error(ex);
				dispatch({ type: 'FETCH_FAIL', payload: getError(ex) });
				toast.error(getError(ex));
			}
		};
		fetchData();
	}, [debtId, preloadAccountId, preloadContractId, preloadAssetSaleId]);

	useEffect(() => {
		generateDate();
		dateHandler();
	}, [date, selectedMonthOption, dueDay, numMonths, firstDueExpiration]);

	// Hydrate the form from the debt being edited, once the supporting lists
	// are available (the owner/contract/assetSale typeaheads need their options
	// loaded before a selection can be shown).
	useEffect(() => {
		if (!isEditing || !resolvedInitialData || hydrated) return;
		if (!preloadAccountId && accounts.length === 0) return;
		if (concepts.length === 0) return;
		const data = resolvedInitialData;
		if (data.contract && typeof data.contract === 'string' && contracts.length === 0) return;
		if (data.assetSale && typeof data.assetSale === 'string' && assetSales.length === 0) return;

		const normalized = data.concept ? normalizeConceptName(data.concept) : undefined;
		setSelectedConcept(normalized);
		setConcept(data.concept);
		if (data.amount !== undefined && data.amount !== null) setAmount(data.amount);
		if (data.capital !== undefined && data.capital !== null && data.capital !== '') setCapital(data.capital);
		if (data.interest !== undefined && data.interest !== null && data.interest !== '') setInterest(data.interest);
		if (data.dues) setDues(data.dues);
		if (data.date) setDate(moment(data.date).toDate());
		if (data.periodicity) setPeriodicity(data.periodicity);
		if (data.periodicityNumMonths) setPeriodicityNumMonths(data.periodicityNumMonths);
		if (data.currency) setCurrency(data.currency);
		if (data.endDate) setEndDate(moment(data.endDate).toDate());
		if (data.dueDay) setDueDay(String(data.dueDay));
		if (data.selectedMonthOption) setSelectedMonthOption(data.selectedMonthOption);
		if (data.numMonths) setNumMonths(data.numMonths);
		if (data.firstDueExpiration) setFirstDueExpiration(moment.utc(data.firstDueExpiration).format('YYYY-MM-DD'));
		setHasPreviousDebts(!!data.hasPreviousDebts);
		if (data.previousDebt) setPreviousDebt(data.previousDebt);
		if (data.previousDebtDate) setPreviousDebtDate(moment(data.previousDebtDate).toDate());
		if (data.billingType) setBillingType(data.billingType);
		if (data.pricePerUser !== undefined && data.pricePerUser !== null) setPricePerUser(data.pricePerUser);
		if (data.lastConfirmedUserCount !== undefined && data.lastConfirmedUserCount !== null) setInitialUserCount(data.lastConfirmedUserCount);
		// Skip when an account/contract/assetSale was just injected via
		// ?accountId=/?contractId=/?assetSaleId= (see the effects below) —
		// otherwise whichever of the two writers resolves last wins,
		// nondeterministically. injectedAtMount is a stable snapshot (unlike
		// the live injected*Id above, which syncInjectedParam rewrites as the
		// user edits), so this guard doesn't flip mid-life.
		if (data.owner && !injectedAtMount.current.accountId) {
			const ownerObj = typeof data.owner === 'object'
				? data.owner
				: accounts.find((a) => a._id === data.owner);
			if (ownerObj) {
				setOwner(ownerObj);
				if (!data.currency && ownerObj.currency) setCurrency(ownerObj.currency);
			}
		}
		if (data.contract && !injectedAtMount.current.contractId) {
			const contractObj = typeof data.contract === 'object'
				? data.contract
				: (filteredContracts || contracts).find((c) => c._id === data.contract);
			if (contractObj) setContract([contractObj]);
		}
		if (data.assetSale && !injectedAtMount.current.assetSaleId) {
			const assetSaleObj = typeof data.assetSale === 'object'
				? data.assetSale
				: assetSales.find((s) => s._id === data.assetSale);
			if (assetSaleObj) setAssetSale([assetSaleObj]);
		}
		const nextMeta = getMeta(normalized, conceptMetaMap, FRONTEND_LEGACY_DEBT_META);
		if (behaviorNeedsCapital(nextMeta.behavior) || nextMeta.treatAsExpense) {
			setDisabledInput(false);
		}
		setHydrated(true);
	}, [isEditing, resolvedInitialData, hydrated, accounts, contracts, assetSales, concepts, conceptMetaMap, preloadAccountId, filteredContracts]);

	// Auto-select an account created via the "crear nueva cuenta" link
	// (CustomTypeahead linkToCreate -> NewAccount.jsx, which returns here with
	// ?accountId=<id>). The ref only de-dupes the fetch — StrictMode
	// double-invokes this in development.
	useEffect(() => {
		if (!injectedAccountId || consumedAccountId.current === injectedAccountId) return;
		consumedAccountId.current = injectedAccountId;

		(async () => {
			try {
				const { data } = await accountService.getDetails(injectedAccountId);
				setOwner(data);
				setIsInvalid(false);
				if (data.currency) setCurrency(data.currency);
				clearFieldError('owner');
				clearFieldError('currency');
			} catch (error) {
				console.error(error);
				toast.error(t('msg.accountLoadFailed'));
			}
		})();
	}, [injectedAccountId]);

	// Mirrors the account effect for a contract created via "crear nuevo
	// alquiler/contrato" (-> NewContract.jsx, returns with ?contractId=<id>).
	useEffect(() => {
		if (!injectedContractId || consumedContractId.current === injectedContractId) return;
		consumedContractId.current = injectedContractId;

		(async () => {
			try {
				const { data } = await contractService.getDetails(injectedContractId);
				setContract([data]);
				setIsPropertyInvalid(false);
				setOwner(data.owner);
				setCurrency(data.currency);
				clearFieldError('contract');
				clearFieldError('owner');
				clearFieldError('currency');
				// Mirrors handlePropertyChange (the non-"gasto" contract flow) — a
				// "gasto" contract selection doesn't touch amount/endDate.
				if (!meta.treatAsExpense) {
					setAmount(data.amount);
					setEndDate(data.periodEnd);
					setDisabledEndDate(true);
					setDisabledAmount(true);
				}
			} catch (error) {
				console.error(error);
				toast.error(t('msg.contractLoadFailed'));
			}
		})();
	}, [injectedContractId]);

	// Mirrors the account effect for an asset sale created via "crear nueva
	// venta de activo fijo" (-> NewAssetSale.jsx, returns with ?assetSaleId=<id>).
	useEffect(() => {
		if (!injectedAssetSaleId || consumedAssetSaleId.current === injectedAssetSaleId) return;
		consumedAssetSaleId.current = injectedAssetSaleId;

		(async () => {
			try {
				const { data } = await assetSaleService.getDetails(injectedAssetSaleId);
				setAssetSale([data]);
				setIsAssetInvalid(false);
				setOwner(data.purchaser);
				setCurrency(data.currency);
				clearFieldError('assetSale');
				clearFieldError('owner');
				clearFieldError('currency');
			} catch (error) {
				console.error(error);
				toast.error(t('msg.assetSaleLoadFailed'));
			}
		})();
	}, [injectedAssetSaleId]);

	// Restores the concept selection lost when the "crear nueva..." round trip
	// remounts the editor. Create mode only — edit mode hydrates concept from
	// the debt being edited. Waits for fetchDebtConcepts() to populate `concepts`.
	useEffect(() => {
		if (isEditing || !injectedConcept || consumedConcept.current === injectedConcept) return;
		if (concepts.length === 0) return;
		if (!concepts.includes(injectedConcept)) return;
		consumedConcept.current = injectedConcept;
		handleSelectedConcept({ target: { value: injectedConcept } });
	}, [isEditing, injectedConcept, concepts]);

	const dateHandler = () => {
		if (moment.utc(firstDueExpiration).isBefore(moment.utc(currentDate))) {
			setCreateWithPreviousDues(true);
		} else {
			setCreateWithPreviousDues(false);
			setCreateWithPreviousPayments(false);
		}
	};

	const generateDate = () => {
		if (!date) return;
		const currentDate = moment.utc(date, 'YYYY-MM-DD');
		let futureDate;

		if (selectedMonthOption === 'Mes actual') {
			futureDate = currentDate.clone();
		} else if (selectedMonthOption === 'Mes siguiente') {
			futureDate = currentDate.clone().add(1, 'month');
		} else if (selectedMonthOption === 'En tres meses' || selectedMonthOption === 'En seis meses') {
			const monthsToAdd = selectedMonthOption === 'En tres meses' ? 3 : 6;
			futureDate = currentDate.clone().add(monthsToAdd, 'month');
		} else if (selectedMonthOption === 'Otro') {
			futureDate = currentDate.clone().add(numMonths, 'month');
		}

		if (futureDate) {
			futureDate.date(Math.min(dueDay, futureDate.daysInMonth()));
			setFirstDueExpiration(moment.utc(futureDate).format('YYYY-MM-DD'));
		}
	};

	const validateCommonFields = () => {
		const emptyFields = [];
		if (!date) emptyFields.push('Fecha');
		if (!selectedConcept && !concept) emptyFields.push('Concepto');
		if (selectedConcept === 'otro' && !concept) emptyFields.push('Otro Concepto');
		if (isPerUser) {
			if (!pricePerUser) emptyFields.push('Precio por usuario');
		} else if (!amount) {
			emptyFields.push('Monto');
		}
		if (!owner) emptyFields.push('Cuenta');
		if (!currency) emptyFields.push('Moneda');
		if (!isEditing && !firstDueExpiration) emptyFields.push('Datos generación cuota');
		return emptyFields;
	};

	const validateBusinessRules = () => {
		if (!isEditing && moment.utc(firstDueExpiration).isBefore(moment.utc(date))) {
			toast.error(t('msg.expirationBeforeDate'));
			return false;
		}

		if (meta.requiresAssetSale && assetSale && assetSale.length > 0) {
			const sumDebtsFloat = parseFloat(assetSale[0].sumDebts);
			const capitalFloat = parseFloat(capital);
			const amountFloat = parseFloat(assetSale[0].amount);
			const initialPaymentFloat = parseFloat(assetSale[0].initialPayment);
			if (sumDebtsFloat + capitalFloat + initialPaymentFloat > amountFloat) {
				const msg = t('msg.assetSaleCapitalExceeded');
				toast.error(msg);
				return false;
			}
		}

		return true;
	};

	const getFieldErrors = () => {
		const emptyFields = [
			...validateCommonFields(),
			...validateByMeta(meta, { periodicity, capital, interest, dues, amount, contract, assetSale }),
		];
		const errors = {};
		emptyFields.forEach((label) => {
			const key = FIELD_ERROR_KEYS[label];
			if (key) errors[key] = REQUIRED_FIELD_MESSAGE;
		});
		return { errors, emptyFields };
	};

	const buildBaseRequestData = () => {
		const base = {
			amount,
			concept,
			date: moment.utc(date),
			owner: owner[0] || owner,
			currency,
			periodicity,
		};
		if (!isEditing) {
			base.dueDay = dueDay;
			base.selectedMonthOption = selectedMonthOption;
			base.firstDueExpiration = moment.utc(firstDueExpiration);
			base.createWithPreviousDues = createWithPreviousDues;
			base.createWithPreviousPayments = createWithPreviousPayments;
		}
		if (isPerUser) {
			base.billingType = 'perUser';
			base.pricePerUser = Number(pricePerUser);
			base.lastConfirmedUserCount = Number(initialUserCount) || 0;
			base.amount = Number(pricePerUser) * (Number(initialUserCount) || 0) || Number(pricePerUser);
		}
		return base;
	};

	const processCommonFields = (requestData) => {
		if (!isEditing && selectedMonthOption === 'Otro') {
			requestData.numMonths = numMonths;
		}
		if (periodicity === 'otra') {
			requestData.periodicityNumMonths = periodicityNumMonths;
		}
		if (hasPreviousDebts) {
			requestData.hasPreviousDebts = hasPreviousDebts;
			requestData.previousDebt = previousDebt;
			requestData.previousDebtDate = previousDebtDate;
		} else {
			requestData.hasPreviousDebts = false;
			requestData.previousDebt = '';
			requestData.previousDebtDate = '';
		}
		return requestData;
	};

	const buildRequestData = () => {
		let requestData = buildBaseRequestData();
		requestData = buildConceptData(
			meta,
			{ contract, assetSale, endDate, interest, dues, capital, periodicity },
			requestData
		);
		requestData = processCommonFields(requestData);
		return requestData;
	};

	const handleSave = async (event) => {
		event.preventDefault();
		if (loading || saving) {
			return;
		}

		const { errors, emptyFields } = getFieldErrors();
		if (emptyFields.length > 0) {
			setFieldErrors(errors);
			dispatch({ type: 'SAVE_FAIL', payload: `${t('msg.completeFields')}: ${emptyFields.join(', ')}.` });
			return;
		}
		setFieldErrors({});

		if (!validateBusinessRules()) {
			return;
		}

		try {
			dispatch({ type: 'SAVE_REQUEST' });

			if (!owner) {
				toast.error(t('msg.selectAccount'));
				dispatch({ type: 'SAVE_FAIL', payload: '' });
				return;
			}

			const requestData = buildRequestData();
			let savedDebtId = debtId || resolvedInitialData?._id;

			if (isEditing) {
				const result = await debtService.edit(savedDebtId, requestData);
				savedDebtId = result.data?.debt?._id || savedDebtId;
				dispatch({ type: 'SAVE_SUCCESS' });
				toast.success(t('msg.debtUpdated'));
			} else {
				const result = await debtService.create({
					...requestData,
					createWithPreviousDues,
					createWithPreviousPayments,
				});
				savedDebtId = result.data?.debt?._id || savedDebtId;
				dispatch({ type: 'SAVE_SUCCESS' });
				toast.success(t('msg.debtCreated'));
			}

			if (onSaved) {
				onSaved(savedDebtId);
			}
		} catch (ex) {
			const message = getError(ex);
			dispatch({ type: 'SAVE_FAIL', payload: message });
			toast.error(message);
		}
	};

	const handleSelectedConcept = (e) => {
		const value = e.target.value;
		syncInjectedParam('concept', value, consumedConcept);
		setSelectedConcept(value);
		clearFieldError('concept');
		clearFieldError('otroConcepto');
		const nextMeta = getMeta(value, conceptMetaMap, FRONTEND_LEGACY_DEBT_META);
		if (behaviorNeedsCapital(nextMeta.behavior) || nextMeta.treatAsExpense) {
			setDisabledInput(false);
		}
		if (nextMeta.defaultPeriodicity) {
			setPeriodicity(nextMeta.defaultPeriodicity);
			clearFieldError('periodicity');
			if (nextMeta.defaultPeriodicity === 'efectivo') {
				setDues(1);
				clearFieldError('dues');
			}
		}
		if (value !== 'otro') {
			setConcept(value);
		}
	};

	function AmountCalc() {
		if (meta.behavior === 'amortizable' || meta.behavior === 'installment-sale') {
			getValorDeCuotaFijaMensualAmortizable();
			setDisabledAmount(false);
		}
		if (meta.behavior === 'interest-only') {
			getValorDeCuotaFijaMensualInteres();
		}
	}

	function getValorDeCuotaFijaMensualAmortizable() {
		if (!capital || isNaN(parseInt(interest)) || !dues) {
			toast.error(t('msg.enterCapitalInterestDues'));
			return;
		} else {
			if (interest == 0) {
				const valorCuota = capital / dues;
				setAmount(valorCuota);
			} else {
				const tasa = interest / 100;
				const valorCuota =
					capital * ((tasa * Math.pow(1 + tasa, dues)) / (Math.pow(1 + tasa, dues) - 1));
				setAmount(Math.ceil(valorCuota));
			}
		}
	}

	function getValorDeCuotaFijaMensualInteres() {
		if (!capital || !interest) {
			toast.error(t('msg.enterCapitalInterest'));
			return;
		}
		const tasa = interest / 100;
		const valorCuota = capital * tasa;
		setAmount(Math.ceil(valorCuota));
	}

	const handleAccountChange = (selected) => {
		if (!selected || selected.length === 0) {
			syncInjectedParam('accountId', null, consumedAccountId);
			setIsInvalid(false);
			setOwner('');
			setCurrency('');
		} else {
			const selectedAccount = selected[0];
			const isValid = selectedAccount ? accountOptions.includes(selectedAccount) : false;
			syncInjectedParam('accountId', selectedAccount?._id, consumedAccountId);
			setIsInvalid(!isValid);
			setOwner(selectedAccount);
			setCurrency(selectedAccount.currency);
			clearFieldError('owner');
			clearFieldError('currency');
		}
	};

	const handlePropertyChange = (selected) => {
		if (!selected || selected.length === 0) {
			const selectedProperty = selected[0];
			const isPropertyInvalid = contractOptions.includes(selectedProperty);
			syncInjectedParam('contractId', null, consumedContractId);
			setIsPropertyInvalid(!isPropertyInvalid);
			setContract(null);
			setOwner('');
			setAmount('');
			setCurrency('');
			setDisabledAmount(false);
			setDisabledEndDate(false);
		} else {
			const selectedProperty = selected[0];
			const isPropertyInvalid = contractOptions.includes(selectedProperty);
			syncInjectedParam('contractId', selectedProperty?._id, consumedContractId);
			setIsPropertyInvalid(!isPropertyInvalid);
			setContract(selected);
			setOwner(selectedProperty.owner);
			setAmount(selectedProperty.amount);
			setCurrency(selectedProperty.currency);
			setEndDate(selectedProperty.periodEnd);
			setDisabledEndDate(true);
			setDisabledAmount(true);
			clearFieldError('contract');
			clearFieldError('owner');
			clearFieldError('amount');
			clearFieldError('currency');
		}
	};

	const handleAssetSaleChange = (selected) => {
		if (!selected || selected.length === 0) {
			const selectedAssetSale = selected[0];
			const isAssetInvalid = assetSaleOptions.includes(selectedAssetSale);
			syncInjectedParam('assetSaleId', null, consumedAssetSaleId);
			setIsAssetInvalid(!isAssetInvalid);
			setAssetSale(null);
			setOwner('');
			setAmount('');
			setCurrency('');
			setDisabledAmount(false);
			setDisabledEndDate(false);
		} else {
			const selectedAssetSale = selected[0];
			const isAssetInvalid = assetSaleOptions.includes(selectedAssetSale);
			syncInjectedParam('assetSaleId', selectedAssetSale?._id, consumedAssetSaleId);
			setIsAssetInvalid(!isAssetInvalid);
			setAssetSale(selected);
			setOwner(selected[0].purchaser);
			setCurrency(selected[0].currency);
			clearFieldError('assetSale');
			clearFieldError('owner');
			clearFieldError('currency');
		}
	};

	const handleContractForGastoChange = (selected) => {
		if (!selected || selected.length === 0) {
			syncInjectedParam('contractId', null, consumedContractId);
			setContract(null);
			setOwner('');
			setCurrency('');
		} else {
			const selectedContract = selected[0];
			syncInjectedParam('contractId', selectedContract?._id, consumedContractId);
			setContract(selected);
			setOwner(selectedContract.owner);
			setCurrency(selectedContract.currency);
			clearFieldError('contract');
			clearFieldError('owner');
			clearFieldError('currency');
		}
	};

	const assetSaleRemaining =
		meta.requiresAssetSale && assetSale && assetSale.length > 0
			? parseFloat(assetSale[0].amount) - parseFloat(assetSale[0].sumDebts) - parseFloat(assetSale[0].initialPayment)
			: null;

	if (loading) {
		return <DetailPageLoader entityLabel='editor' />;
	}

	const resolvedSaveLabel = saveLabel || (isEditing ? 'Guardar' : 'Agregar');

	// Round-tripped into the "crear nueva..." links below so the created
	// record's id (and the current concept, which drives which fields render)
	// come back via linkToCreate -> NewAccount.jsx/NewContract.jsx/NewAssetSale.jsx.
	// location.search already carries `concept` — handleSelectedConcept keeps
	// it in sync via syncInjectedParam — so it just needs to ride along here.
	const returnToParam = encodeURIComponent(location.pathname + location.search);

	return (
		<Form id='debt-editor-form' onSubmit={handleSave} className='tw:space-y-4'>
			<div className='tw:sticky tw:top-14 tw:z-20 tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-3 tw:rounded-panel tw:border tw:border-border tw:bg-surface/95 tw:px-5 tw:py-3.5 tw:shadow-sm tw:backdrop-blur'>
				<div className='tw:text-[12.5px] tw:text-ink-2'>
					{isEditing ? 'Actualizá los datos de la deuda.' : 'Completá los datos para crear la deuda.'}
				</div>
				<div className='tw:flex tw:gap-2'>
					<Button icon={<MdArrowBack />} onClick={onCancel} disabled={saving}>
						{cancelLabel}
					</Button>
					{onReturn && (
						<Button icon={<MdKeyboardReturn />} onClick={onReturn} disabled={saving}>
							{returnLabel || t('action.back')}
						</Button>
					)}
					<Button
						variant='primary'
						icon={isEditing ? <FaRegSave /> : <MdAdd />}
						type='submit'
						disabled={saving || loading}
					>
						{saving ? 'Guardando...' : resolvedSaveLabel}
					</Button>
				</div>
			</div>

			{error && (
				<div className='tw:rounded-[7px] tw:border tw:border-danger tw:bg-danger-soft tw:px-4 tw:py-3 tw:text-[13px] tw:text-danger'>
					{error}
				</div>
			)}

			<Section
				title={t('title.dueGenerationData')}
				description={isEditing ? 'La fecha de inicio y el cronograma de generación no se editan aquí — usá "Editar fecha" en el detalle de la deuda.' : undefined}
			>
				<div className='tw:grid tw:grid-cols-1 tw:gap-4 md:tw:grid-cols-4'>
					{isEditing ? (
						<>
							<FormField id='debt-date' label={t('form.debtStartDateLong')}>
								<Form.Control
									id='debt-date'
									className={controlClass}
									value={date ? moment.utc(date).format('DD-MM-YYYY') : t('msg.noData')}
									disabled
								/>
							</FormField>
							<FormField id='debt-due-day' label={t('form.firstDueDay')}>
								<Form.Control id='debt-due-day' className={controlClass} value={dueDay || t('msg.noData')} disabled />
							</FormField>
							<FormField id='debt-generate-from' label={t('form.generateFrom')}>
								<Form.Control
									id='debt-generate-from'
									className={controlClass}
									value={selectedMonthOption || t('msg.noData')}
									disabled
								/>
							</FormField>
						</>
					) : (
						<>
							<FormField id='debt-date' label={t('form.debtStartDate')} required>
								<DatePicker
									id='debt-date'
									selected={date}
									onChange={(d) => {
										setDate(d);
										clearFieldError('date');
										clearFieldError('firstDueExpiration');
									}}
									dateFormat='dd-MM-yyyy'
									className={`form-control ${controlClass}`}
									placeholderText='dd-mm-yyyy'
								/>
								<FieldError message={fieldErrors.date} />
							</FormField>
							<FormField id='debt-due-day' label={t('form.firstDueDay')} required>
								<Select
									id='debt-due-day'
									options={dueDays}
									value={dueDay}
									onChange={(e) => setDueDay(e.target.value)}
								/>
							</FormField>
							<FormField id='debt-generate-from' label={t('form.generateFrom')} required>
								<Select
									id='debt-generate-from'
									options={monthOptions}
									value={selectedMonthOption}
									onChange={(e) => setSelectedMonthOption(e.target.value)}
								/>
								<FieldError message={fieldErrors.firstDueExpiration} />
							</FormField>
							{selectedMonthOption === 'Otro' && (
								<FormField id='debt-num-months' label={t('form.numMonths')} required>
									<Form.Control
										id='debt-num-months'
										className={controlClass}
										type='number'
										min='1'
										max='240'
										value={numMonths}
										onChange={(e) => setNumMonths(parseInt(e.target.value, 10))}
										onInput={(e) => {
											const inputValue = parseInt(e.target.value, 10);
											const maxValue = 240;
											if (maxValue !== null && inputValue > maxValue) {
												e.target.value = maxValue;
											}
										}}
									/>
								</FormField>
							)}
						</>
					)}
				</div>
				{date && (
					<p className='tw:mt-3 tw:mb-0 tw:text-[12.5px] tw:text-ink-2'>
						{`${t('msg.firstExpiration')}: ${moment.utc(firstDueExpiration).format('DD-MM-YYYY')}`}
					</p>
				)}
			</Section>

			{createWithPreviousDues && !isEditing && (
				<Section title={t('title.debtWithPreviousDues')} description={t('msg.previousDuesWarning')}>
					<Checkbox
						label={t('form.markPreviousDuesPaid')}
						checked={createWithPreviousPayments}
						onChange={() => setCreateWithPreviousPayments(!createWithPreviousPayments)}
					/>
				</Section>
			)}

			<Section title={t('title.accountingInfo')}>
				<div className='tw:grid tw:grid-cols-1 tw:gap-4 md:tw:grid-cols-2'>
					<FormField id='debt-concept' label={t('entity.concept')} required>
						<Select
							id='debt-concept'
							options={concepts.map((c) => ({ value: c, label: conceptMap[c] || c }))}
							value={selectedConcept}
							onChange={handleSelectedConcept}
							placeholder={t('entity.concept')}
						/>
						<FieldError message={fieldErrors.concept} />
					</FormField>
					{meta.requiresContract && !meta.treatAsExpense && !preloadContractId && (
						<FormField id='debt-contract' label={t('field.contract')} required>
							<CustomTypeahead
								id='contracts'
								labelKey={(option) => `${option.customId}`}
								onChange={handlePropertyChange}
								options={contractOptions}
								selected={contract ? contract : []}
								placeholder={t('form.placeholder.searchContract')}
								linkToCreate={`/AdminScreen/newContract?returnTo=${returnToParam}`}
								newOption='nuevo alquiler'
								variant='entity'
							/>
							{isPropertyInvalid && <div className='tw:mt-1 tw:text-[12.5px] tw:text-danger'>{t('msg.invalidContract')}</div>}
							<FieldError message={fieldErrors.contract} />
						</FormField>
					)}
					{meta.requiresContract && !meta.treatAsExpense && preloadContractId && (
						<FormField id='debt-contract' label={t('field.contract')}>
							<Form.Control id='debt-contract' className={controlClass} readOnly value={contract ? contract[0].customId : ''} />
						</FormField>
					)}
					{meta.requiresAssetSale && !preloadAssetSaleId && (
						<FormField id='debt-asset-sale' label={t('entity.assetSale')} required>
							<CustomTypeahead
								id='assetSaleService'
								labelKey={(option) => `${option.customId}`}
								onChange={handleAssetSaleChange}
								options={assetSaleOptions}
								selected={assetSale ? assetSale : []}
								placeholder={t('form.placeholder.searchAssetSale')}
								linkToCreate={`/AdminScreen/newAssetSale?returnTo=${returnToParam}`}
								newOption='nueva venta de activo fijo'
								variant='entity'
							/>
							{isAssetInvalid && <div className='tw:mt-1 tw:text-[12.5px] tw:text-danger'>{t('msg.invalidContract')}</div>}
							<FieldError message={fieldErrors.assetSale} />
						</FormField>
					)}
					{meta.requiresAssetSale && preloadAssetSaleId && (
						<FormField id='debt-asset-sale' label={t('entity.assetSale')}>
							<Form.Control id='debt-asset-sale' className={controlClass} readOnly value={assetSale ? assetSale[0].customId : ''} />
						</FormField>
					)}
					{meta.requiresContract && meta.treatAsExpense && !preloadContractId && (
						<FormField id='debt-contract-gasto' label={t('field.contract')} required>
							<CustomTypeahead
								id='contracts-gasto'
								labelKey={(option) => `${option.customId}`}
								onChange={handleContractForGastoChange}
								options={contractOptions}
								selected={contract ? contract : []}
								placeholder={t('form.placeholder.searchContractGeneric')}
								linkToCreate={`/AdminScreen/newContract?returnTo=${returnToParam}`}
								newOption='nuevo contrato'
								variant='entity'
							/>
							<FieldError message={fieldErrors.contract} />
						</FormField>
					)}
					{meta.requiresContract && meta.treatAsExpense && preloadContractId && (
						<FormField id='debt-contract-gasto' label={t('field.contract')}>
							<Form.Control id='debt-contract-gasto' className={controlClass} readOnly value={contract ? contract[0].customId : ''} />
						</FormField>
					)}
					{meta.behavior === 'deposit' ? (
						<FormField id='debt-owner' label={t('entity.account.debtor')} required>
							<Form.Control id='debt-owner' className={controlClass} readOnly value={owner ? (owner.name || owner) : ''} />
							<FieldError message={fieldErrors.owner} />
						</FormField>
					) : (
						<FormField id='debt-owner' label={t('entity.account.debtor')} required>
							<CustomTypeahead
								id='accounts'
								labelKey={(option) => `${option.name}`}
								onChange={handleAccountChange}
								options={accountOptions}
								disabled={!!preloadAccountId}
								selected={owner ? [owner] : []}
								placeholder={t('form.placeholder.searchAccount')}
								linkToCreate={`/AdminScreen/newAccount?returnTo=${returnToParam}`}
								newOption='nueva cuenta'
								variant='entity'
							/>
							{isInvalid && <div className='tw:mt-1 tw:text-[12.5px] tw:text-danger'>{t('msg.invalidAccount')}</div>}
							<FieldError message={fieldErrors.owner} />
						</FormField>
					)}
					<FormField id='debt-currency' label={t('field.currency')} required>
						<Select
							id='debt-currency'
							options={currencies}
							value={currency}
							onChange={(e) => {
								setCurrency(e.target.value);
								clearFieldError('currency');
							}}
							placeholder={t('field.currency')}
						/>
						<FieldError message={fieldErrors.currency} />
					</FormField>
					<FormField id='debt-periodicity' label={t('field.periodicity')} required>
						<Select
							id='debt-periodicity'
							options={periodicities}
							value={periodicity}
							onChange={(e) => {
								setPeriodicity(e.target.value);
								clearFieldError('periodicity');
								if (e.target.value === 'efectivo') {
									setDues(1);
									clearFieldError('dues');
								}
							}}
						/>
						<FieldError message={fieldErrors.periodicity} />
					</FormField>
					{periodicity === 'otra' && (
						<FormField id='debt-periodicity-months' label={t('form.periodicityMonths')}>
							<FormatedNumberInput
								className={controlClass}
								onChange={(e) => setPeriodicityNumMonths(e.target.value)}
								min={1}
								noDecimal={true}
							/>
						</FormField>
					)}
					{showBillingBlock && (
						<>
							<FormField id='debt-billing-type' label={t('form.billingType')} required>
								<Select
									id='debt-billing-type'
									options={[
										{ value: 'fixed', label: t('form.billingType.fixed') },
										{ value: 'perUser', label: t('form.billingType.perUser') },
									]}
									value={billingType}
									onChange={(e) => setBillingType(e.target.value)}
								/>
							</FormField>
							{isPerUser ? (
								<>
									<FormField id='debt-price-per-user' label={t('form.pricePerUser')} required>
										<FormatedNumberInput
											className={controlClass}
											value={pricePerUser}
											onChange={(e) => {
												setPricePerUser(e.target.value);
												clearFieldError('pricePerUser');
											}}
											min={0}
										/>
										<FieldError message={fieldErrors.pricePerUser} />
									</FormField>
									<FormField id='debt-initial-user-count' label={t('form.initialUserCount')}>
										<FormatedNumberInput
											className={controlClass}
											value={initialUserCount}
											onChange={(e) => setInitialUserCount(e.target.value)}
											min={0}
											noDecimal={true}
										/>
									</FormField>
									<p className='tw:text-[12.5px] tw:text-ink-2 tw:self-center'>
										{`${t('msg.provisionalMonthlyAmount')}: ${(Number(pricePerUser) || 0) * (Number(initialUserCount) || 0)}. ${t('msg.duePendingUntilCount')}`}
									</p>
								</>
							) : (
								<FormField id='debt-amount' label={t('field.amount')} required>
									<FormatedNumberInput
										className={controlClass}
										value={amount}
										onChange={(e) => {
											setAmount(e.target.value);
											clearFieldError('amount');
										}}
										min={0}
									/>
									<FieldError message={fieldErrors.amount} />
								</FormField>
							)}
						</>
					)}
					{selectedConcept && behaviorNeedsCapital(meta.behavior) && (
						<>
							<FormField id='debt-capital' label={t('field.capital')} required>
								<FormatedNumberInput
									className={controlClass}
									onChange={(e) => {
										setCapital(e.target.value);
										clearFieldError('capital');
									}}
									min={0}
								/>
								{assetSaleRemaining !== null && (
									<p
										className='tw:mt-1 tw:mb-0 tw:text-[12.5px]'
										style={{ color: parseFloat(capital) > assetSaleRemaining ? 'var(--tw-color-danger)' : undefined }}
									>
										{t('msg.maxAvailableCapital')}: {assetSaleRemaining.toLocaleString('es-UY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
									</p>
								)}
								<FieldError message={fieldErrors.capital} />
							</FormField>
							{behaviorNeedsInterest(meta.behavior) && periodicity !== 'efectivo' && (
								<FormField id='debt-interest' label={t('form.interestPercent')} required>
									<FormatedNumberInput
										className={controlClass}
										value={interest}
										onChange={(e) => {
											setInterest(e.target.value);
											clearFieldError('interest');
										}}
										min={0}
									/>
									<FieldError message={fieldErrors.interest} />
								</FormField>
							)}
							{meta.behavior === 'interest-only' && (
								<FormField id='debt-end-date' label={t('form.endDate')}>
									<DatePicker
										selected={endDate ? moment(endDate, 'DD-MM-YYYY').toDate() : null}
										onChange={(d) => setEndDate(d)}
										dateFormat='dd-MM-yyyy'
										className={`form-control ${controlClass}`}
										placeholderText='dd-mm-yyyy'
										disabled={disabledEndDate}
									/>
								</FormField>
							)}
							{behaviorNeedsDues(meta.behavior) && periodicity !== 'efectivo' && (
								<FormField id='debt-dues' label={t('entity.due.plural')} required>
									<FormatedNumberInput
										className={controlClass}
										onChange={(e) => {
											setDues(e.target.value);
											clearFieldError('dues');
										}}
										min={1}
										noDecimal={true}
									/>
									<FieldError message={fieldErrors.dues} />
								</FormField>
							)}
							<FormField id='debt-amount-calc' label={`${t('field.amount')} ${behaviorNeedsCapital(meta.behavior) ? 'cuota' : ''}`} required>
								<div className='tw:flex tw:gap-2'>
									<FormatedNumberInput
										className={`${controlClass} tw:flex-1`}
										value={amount}
										disabled={disabledAmount}
										onChange={(e) => {
											setAmount(e.target.value);
											clearFieldError('amount');
										}}
										min={0}
									/>
									<Button size='sm' onClick={AmountCalc} disabled={disabledInput}>
										{t('action.calculate')}
									</Button>
								</div>
								<FieldError message={fieldErrors.amount} />
							</FormField>
						</>
					)}
					{meta.behavior === 'deposit' && (
						<>
							{depositNeedsDues(meta.behavior, periodicity) && (
								<FormField id='debt-dues' label={t('entity.due.plural')} required>
									<FormatedNumberInput
										className={controlClass}
										onChange={(e) => {
											setDues(e.target.value);
											clearFieldError('dues');
										}}
										min={1}
										noDecimal={true}
									/>
									<FieldError message={fieldErrors.dues} />
								</FormField>
							)}
							<FormField id='debt-amount' label='Monto' required>
								<FormatedNumberInput
									className={controlClass}
									value={amount}
									onChange={(e) => {
										setAmount(e.target.value);
										clearFieldError('amount');
									}}
									min={0}
								/>
								<FieldError message={fieldErrors.amount} />
							</FormField>
						</>
					)}
					{meta.treatAsExpense && !showBillingBlock && (
						<FormField id='debt-amount' label='Monto' required>
							<FormatedNumberInput
								className={controlClass}
								value={amount}
								onChange={(e) => {
									setAmount(e.target.value);
									clearFieldError('amount');
								}}
								min={0}
							/>
							<FieldError message={fieldErrors.amount} />
						</FormField>
					)}
				</div>
			</Section>

			<Section title={t('title.offSystemRecord')} description={t('msg.offSystemTooltip')}>
				<div className='tw:grid tw:grid-cols-1 tw:gap-4 md:tw:grid-cols-3 tw:items-end'>
					<div className='tw:flex tw:items-center tw:h-[34px]'>
						<Checkbox
							label={t('form.hasPreviousDebt')}
							checked={hasPreviousDebts}
							onChange={(e) => setHasPreviousDebts(e.target.checked)}
						/>
					</div>
					<FormField id='debt-previous-debt' label={t('field.previousDebt')}>
						<FormatedNumberInput
							className={controlClass}
							onChange={(e) => setPreviousDebt(e.target.value)}
							disabled={!hasPreviousDebts}
							min={0}
						/>
					</FormField>
					<FormField id='debt-previous-debt-date' label={t('form.previousDebtDate')}>
						<DatePicker
							selected={previousDebtDate ? previousDebtDate : null}
							onChange={(d) => setPreviousDebtDate(d)}
							dateFormat='dd-MM-yyyy'
							className={`form-control ${controlClass}`}
							placeholderText='dd-mm-yyyy'
							disabled={!hasPreviousDebts}
						/>
					</FormField>
				</div>
			</Section>
		</Form>
	);
};

DebtEditor.propTypes = {
	mode: PropTypes.oneOf(['create', 'edit']),
	debtId: PropTypes.string,
	initialData: PropTypes.object,
	onCancel: PropTypes.func.isRequired,
	onSaved: PropTypes.func.isRequired,
	cancelLabel: PropTypes.string,
	saveLabel: PropTypes.string,
	onReturn: PropTypes.func,
	returnLabel: PropTypes.string,
	preloadAccountId: PropTypes.string,
	preloadContractId: PropTypes.string,
	preloadAssetSaleId: PropTypes.string,
};

export default DebtEditor;
